import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { serverEncryption } from "@/shared/utils/serverEncryption";
interface SaveSSNParams {
  ssn: string;
  estateId: number; // Updated to use estate_id instead of DeceasedUserData
}

interface UseSaveSSNResult {
  saveSSN: (params: SaveSSNParams) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to encrypt a person's SSN and store it in Supabase using server-side encryption
 * This handles the full flow of:
 * 1. Encrypting the SSN using server-side encryption with person context
 * 2. Updating the persons table in Supabase with ssn_encrypted
 */
export function useSaveSSN(): UseSaveSSNResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ ssn, estateId }: SaveSSNParams) => {
      if (!estateId) {
        throw new Error("Person ID is required");
      }

      const encryptedSSN = await serverEncryption.encrypt(ssn, "person", estateId, "ssn");

      const { error } = await supabase
        .from("estates")
        .update({
          ssn_encrypted: encryptedSSN,
        })
        .eq("estate_id", estateId);

      if (error) {
        console.error("Database error saving SSN:", error);
        throw new Error(`Failed to save encrypted SSN: ${error.message}`);
      }
    },
    onSuccess: (_result, { estateId }) => {
      queryClient.invalidateQueries({
        queryKey: ["personData", estateId],
      });
      queryClient.invalidateQueries({
        queryKey: ["estates"],
      });
      queryClient.invalidateQueries({
        queryKey: ["deceasedSSNData", estateId],
      });
    },
  });

  return {
    saveSSN: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error?.message || null,
  };
}
